import { getRebranding } from '../hooks/useRebranding';
import { EWidgetVersion } from '../types/enums';

/**
 * Picks the version to render from the versions the caller supports (each caller owns its own set):
 * no version → the oldest supported, a supported one → itself, any other → the latest supported.
 *
 * @param version version sent by the server (`TWidget.version`)
 * @param supported versions the caller implements; must not be empty
 */
export const resolveVersion = <T extends number>(version: number | null | undefined, supported: readonly T[]): T => {
    if (version == null) {
        return Math.min(...supported) as T;
    }
    return supported.includes(version as T) ? (version as T) : (Math.max(...supported) as T);
};

const V1_V2: readonly EWidgetVersion[] = [EWidgetVersion.V1, EWidgetVersion.V2];

/**
 * Whether a widget with a v1 and a v2 implementation renders v2 (see {@link resolveVersion}): no version is v1, an
 * unknown one is v2. Temporary: no version while the rebranding switch is on is v2 (see `getRebranding` and
 * consumer_conventions.md).
 */
export const isV2 = (widget?: { version?: number | null } | null): boolean => {
    if (!widget?.version && getRebranding()) {
        return true;
    }
    return resolveVersion(widget?.version, V1_V2) === EWidgetVersion.V2;
};
